// segmentEditor.js — page-level segment composer
//
// Holds the ruleTree state, persists to marketing_segments/{id}, shows reach panel.
// URL patterns:
//   /app/marketing/segments/new
//   /app/marketing/segments/:id/edit

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button, Card, CardBody, Col,
  Row
} from 'reactstrap';
import { firestore } from '../../../../helpers/Firebase';
import { actorFromUser, computeDiff, writeAuditEntry } from '../shared/auditLog.js';
import DynamicFields from '../shared/DynamicFields.jsx';
import ReachPanel from './components/ReachPanel.jsx';
import RuleGroup from './components/RuleGroup.jsx';
import TemplatePicker from './components/TemplatePicker.jsx';
import { validateRuleTree } from './helpers/segmentResolver.js';

const EMPTY_TREE = () => ({ match: 'all', rules: [{ field: null, op: null, value: undefined }] });

const countLeafRules = (group) => {
  if (!group || !Array.isArray(group.rules)) return 0;
  return group.rules.reduce((acc, r) => acc + (r.rules ? countLeafRules(r) : (r.field ? 1 : 0)), 0);
};

const SegmentEditor = ({ match, history }) => {
  const segmentId = match.params.id || null;
  const isNew = !segmentId;
  const currentUser = useSelector(s => s.authUser?.user);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ruleTree, setRuleTree] = useState(EMPTY_TREE());
  const [original, setOriginal] = useState(null); // baseline for audit diff
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await firestore().collection('marketing_segments').doc(segmentId).get();
        if (cancelled) return;
        if (!snap.exists) {
          setError('Segment not found');
        } else {
          const d = snap.data();
          setName(d.name || '');
          setDescription(d.description || '');
          setRuleTree(d.ruleTree || EMPTY_TREE());
          setOriginal({ name: d.name || '', description: d.description || '', ruleTree: d.ruleTree || EMPTY_TREE() });
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [segmentId, isNew]);

  const validationErrors = validateRuleTree(ruleTree);
  const canSave = name.trim().length > 0 && validationErrors.length === 0 && !saving;

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        ruleTree,
        updatedAt: Date.now(),
        updatedBy: currentUser?.uid || null,
        updatedByName: currentUser?.displayName || currentUser?.email || null,
      };
      let savedId = segmentId;
      if (isNew) {
        payload.createdAt = Date.now();
        payload.createdBy = currentUser?.uid || null;
        payload.createdByName = currentUser?.displayName || currentUser?.email || null;
        const ref = await firestore().collection('marketing_segments').add(payload);
        savedId = ref.id;
        history.replace(`/app/marketing/segments/${ref.id}/edit`);
      } else {
        await firestore().collection('marketing_segments').doc(segmentId).set(payload, { merge: true });
      }

      // Audit log — fire-and-forget
      const actor = actorFromUser(currentUser);
      if (isNew) {
        writeAuditEntry('marketing_segments', savedId, { ...actor, action: 'created' });
      } else if (original) {
        const changes = computeDiff(original, payload);
        if (Object.keys(changes).length > 0) {
          writeAuditEntry('marketing_segments', savedId, { ...actor, action: 'edited', changes });
        }
        setOriginal({ name: payload.name, description: payload.description, ruleTree: payload.ruleTree });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading" />;
  }

  return (
    <>
      {/* Page header — title left, action buttons right (ListPageHeading pattern) */}
      <Row className="mb-3">
        <Col xs="12" md="6">
          <h1 className="mb-0 p-0">{isNew ? 'New segment' : 'Edit segment'}</h1>
        </Col>
        <Col xs="12" md="6">
          <div className="d-flex align-items-center justify-content-end" style={{ gap: 8 }}>
            <Button
              color="danger" outline size="lg"
              onClick={() => history.push('/app/marketing/segments')}
            >
              Dismiss
            </Button>
            <Button
              color="primary" size="lg"
              disabled={!canSave}
              onClick={onSave}
            >
              {saving ? 'Saving…' : (isNew ? 'Create segment' : 'Save changes')}
            </Button>
          </div>
          {error && <div className="text-danger small text-right mt-2">Error: {error}</div>}
        </Col>
      </Row>

      <Row>
        <Col xs="12" md="8">
          <Card className="mb-3">
            <CardBody>
              <DynamicFields
                value={{ name, description }}
                onChange={(v) => { setName(v.name || ''); setDescription(v.description || ''); }}
                elements={[
                  { key: 'name', title: 'Name', type: 'text', width: '12', required: true, maxLength: 100, placeholder: 'e.g. Dormant high-value Dubai users' },
                  { key: 'description', title: 'Description', type: 'textarea', width: '12', rows: 2, maxLength: 500, placeholder: 'What this segment is for, who it targets' },
                ]}
              />
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="d-flex align-items-baseline mb-3" style={{ gap: 8 }}>
                <h6 className="mb-0">Audience rules</h6>
                <span className="text-muted small">
                  {(() => {
                    const count = countLeafRules(ruleTree);
                    return count === 0
                      ? '— no rules yet, pick a template or start from scratch'
                      : `· ${count} rule${count > 1 ? 's' : ''} defined`;
                  })()}
                </span>
              </div>

              {countLeafRules(ruleTree) === 0 && (
                <TemplatePicker
                  currentName={name}
                  onApply={(patch) => {
                    if (patch.ruleTree) setRuleTree(patch.ruleTree);
                    if (patch.name && !name.trim()) setName(patch.name);
                    if (patch.description && !description.trim()) setDescription(patch.description);
                  }}
                />
              )}

              <RuleGroup
                group={ruleTree}
                onChange={setRuleTree}
                onRemove={() => { }}
                depth={0}
              />

              {validationErrors.length > 0 && (
                <div className="text-warning mt-2">
                  <small>
                    <strong>{validationErrors.length} validation issue{validationErrors.length > 1 ? 's' : ''}:</strong>
                    <ul className="mb-0">
                      {validationErrors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </small>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col xs="12" md="4">
          <ReachPanel ruleTree={ruleTree} />

        </Col>
      </Row>
    </>
  );
};

export default SegmentEditor;
